import _ from 'lodash';
import pluralize from 'pluralize';
import {
  AdvancedSearchConfigurationsType,
  ConfigurationsType,
  MissionConfigurationsType,
  SearchFieldType
} from "../types/configurations";
import {
  composeFilters,
  getMissionsConfigurationsBy,
  getProductionSearchFieldsConfigurationsBy,
  isApplicableFieldFromConfigurations,
} from "./configurationHelper";
import {
  CartOrderStatisticsType,
  CartOrderType,
  DownloadType,
  InvalidationJobType,
  JobType, ReprocessingJobType
} from "../types/cart";
import {ShapeType} from "../types/shape";
import {extractFiltersBy, extractFinalValues, hasBeenFilled} from "./dynamicFormHelper";
import CartService from "../services/CartService";
import {ProductImageStacType} from "../types/product";
import {AppParametersType} from "../types/app";
import translations from '../translations';

export const getCartOrderStatisticsDescriptionFromItem = (configurations, item) => {
  return item && item.metadata && item.metadata.properties && item.metadata.properties.mission
};

export const getSummaryMessageByCartOrderStatistics = (
  cartOrderStatistics: CartOrderStatisticsType,
  totalOrdersItemsCount: number,
  appParameters: AppParametersType
):string => {
  const statisticsList: string[] = [];
  let totalItemsCount = 0;
  for (const description in cartOrderStatistics) {
    const {
      area,
      sceneCount,
      itemCount,
    } = cartOrderStatistics[description];
    let countString;
    if (area) {
      countString = `an area of ${_.round(area, 1)}km² for`;
    } else if (sceneCount > 0) {
      countString = `${pluralize('scene', sceneCount, true)} of`;
    } else {
      countString = `${pluralize('item', itemCount, true)} of`;
    }

    totalItemsCount = totalItemsCount + itemCount;
    statisticsList.push(`${countString} ${description}`);
  }

  return totalOrdersItemsCount === totalItemsCount
    ? `${translations('addingCartCartHelper', appParameters.language).replace(`${statisticsList}`, statisticsList.join(', '))}`
    : `${translations('addingCartAOICartHelper', appParameters.language).replace(`${statisticsList}`, statisticsList.join(', '))}`
};


export interface ProductionFieldsConfiguration{
  productionSearchFieldsConfigurations: SearchFieldType[],
  missionConfig: MissionConfigurationsType
}

export const getProductionFieldsConfigurations = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  mainInputConfigurations: {},
  scopeConfigurationFields: string,
  selectedArea: ShapeType,
  searchFormValues,
  selectedProducts: ProductImageStacType[],
  selectedMissions: any = null
): ProductionFieldsConfiguration[] => {
    let result: ProductionFieldsConfiguration[] = [];
    if (!selectedMissions) {
      selectedMissions = searchFormValues.missions;
    }
    if (selectedMissions) {
      const missionsConfigurations = getMissionsConfigurationsBy(configurations as AdvancedSearchConfigurationsType, selectedMissions);
      result = missionsConfigurations.filter((missionConfig) => {
        const values = {
          ...searchFormValues,
          missions: [missionConfig.code]
        };
        const productionSearchFieldsConfigurations = getProductionSearchFieldsConfigurationsBy(configurations, appParameters, scopeConfigurationFields, values);

        return productionSearchFieldsConfigurations && productionSearchFieldsConfigurations.length > 0 && selectedProducts.some((product) => product
            && product.properties && product.properties.mission && product.properties.mission === missionConfig.description) ?
            {
              productionSearchFieldsConfigurations,
              missionConfig,
            } : {};
      }).map((missionConfig) => {
        const values = {
          ...searchFormValues,
          missions: [missionConfig.code]
        };
        const productionSearchFieldsConfigurations = getProductionSearchFieldsConfigurationsBy(configurations, appParameters, scopeConfigurationFields, values);

        return {
          productionSearchFieldsConfigurations,
          missionConfig,
        };
      });
    }
    return result;
};

export const getMixedProductionFields = (productionFieldsConfiguration: ProductionFieldsConfiguration[]): SearchFieldType[] => {
  let mixedProductionFields: SearchFieldType[] = [];
  const mixedFieldsData = {};
  productionFieldsConfiguration.forEach((productionFieldsConfiguration: ProductionFieldsConfiguration) => {
    productionFieldsConfiguration.productionSearchFieldsConfigurations.forEach(productionSearchField => {
      let index = mixedProductionFields.length-1;
      let mixedProductionSearchField = {...productionSearchField};

      if (mixedFieldsData.hasOwnProperty(mixedProductionSearchField.name)) { // il campo esiste già, devo fare un mix (sensato)
        index = mixedFieldsData[mixedProductionSearchField.name];
        // faccio il mio mix...
        mixedProductionSearchField = composeFilters(mixedProductionFields[index], mixedProductionSearchField);
        // lo sovrascrivo nella lista
        mixedProductionFields[index] = mixedProductionSearchField;
      } else {
        mixedProductionFields.push(mixedProductionSearchField);
        index++;
      }
      // lo salvo nella lista
      mixedFieldsData[mixedProductionSearchField.name] = index;
    });
  });
  return mixedProductionFields;
};

export const haveProductionFieldsBeenFilled = (
  configurations: ConfigurationsType,
  appParameters: AppParametersType,
  mainInputConfigurations: {},
  scopeConfigurationFields: string,
  selectedArea: ShapeType,
  searchFormValues,
  productionFields: SearchFieldType[]
): boolean => {
  const values = {
    ...searchFormValues,
  };
  const finalValues: any = extractFinalValues(mainInputConfigurations, configurations, appParameters, scopeConfigurationFields, selectedArea, values);
  return productionFields.length === 0 || productionFields.every((productionField) => {
      const isApplicableField = isApplicableFieldFromConfigurations(configurations, appParameters, scopeConfigurationFields, productionField.name, searchFormValues, selectedArea);
      const fieldHasBeenFilled =  hasBeenFilled(finalValues[productionField.name]);
      return isApplicableField ? fieldHasBeenFilled : true;
  });
}

export const getAllProductImagesInOrdersSubmitted = (
  ordersSubmitted: CartOrderType[]
): ProductImageStacType[] => (
  ordersSubmitted.reduce((acc: ProductImageStacType[], order: CartOrderType) => {
    return acc.concat(order.items.map((orderItem) => orderItem.metadata))
  }, [])
);

export const couldBeAlreadyInTheCart = (
  ordersSubmitted: CartOrderType[],
  productImage: ProductImageStacType
): boolean => false;

export const IsAlreadyInTheCart = (configurations, ordersSubmitted, productImage) => {
  return false;
}

export const submitJob = (
    appParameters: AppParametersType,
    job: JobType | InvalidationJobType | ReprocessingJobType
) => {
  return CartService.submitJob(appParameters, job)
      .then((cardRes) => {
      })
};

export const downloadAllProducts = (
    appParameters: AppParametersType,
    downloads: DownloadType[]
) => {
  Promise.allSettled = Promise.allSettled || ((promises) => Promise.all(
      promises.map(p => p
          .then(value => ({
            status: "fulfilled",
            value
          }))
          .catch(reason => ({
            status: "rejected",
            reason
          }))
      )
  ));
  return Promise.allSettled(
      downloads.map((download) => CartService.downloadProduct(appParameters, download))
  ).then((results: PromiseSettledResult<any>[]) => {
    const valid = downloads.filter((order, index) => results[index].status === "fulfilled")
    const wrong = downloads.filter((order, index) => results[index].status === "rejected")
    const errors = (
        results.filter((result) => result.status === "rejected") as PromiseRejectedResult[]
    ).map((error: PromiseRejectedResult) => error.reason)
    return {
      valid,
      wrong,
      errors
    }
  })
};

export const submitAllJobs = (
    appParameters: AppParametersType,
    jobs: (JobType | InvalidationJobType | ReprocessingJobType)[]
) => {
  Promise.allSettled = Promise.allSettled || ((promises) => Promise.all(
      promises.map(p => p
          .then(value => ({
            status: "fulfilled",
            value
          }))
          .catch(reason => ({
            status: "rejected",
            reason
          }))
      )
  ));
  return Promise.allSettled(
      jobs.map((job) => submitJob(appParameters, job))
  ).then((results: PromiseSettledResult<any>[]) => {
    const valid = jobs.filter((order, index) => results[index].status === "fulfilled")
    const wrong = jobs.filter((order, index) => results[index].status === "rejected")
    const errors = (
        results.filter((result) => result.status === "rejected") as PromiseRejectedResult[]
    ).map((error: PromiseRejectedResult) => error.reason)
    return {
      valid,
      wrong,
      errors
    }
  })
};

export const processableFrames = (selectedProducts, cutscenes, lastShape) => {
  let processableFrames = [];
  if (cutscenes && cutscenes.length) {
    cutscenes.forEach((cutscene) => {
      if (selectedProducts.some((product) => product.id === cutscene.id)) {
        if (lastShape && lastShape.added_on_time && cutscene.aoi_added_on_time === lastShape.added_on_time) {
          processableFrames = processableFrames.concat(cutscene.scenes);
        } else if (cutscene.aoi_added_on_time === null) {
          processableFrames = processableFrames.concat(cutscene.scenes);
        }
      }
    });
  }
  return processableFrames;
}
